import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import CountUp from "@/components/wishire/CountUp";

const CANDIDATE_PAINS = [
  { k: "01", t: "No idea where you stand", d: "Is it your resume? Your aptitude? Your interview? Nobody tells you — until the rejection." },
  { k: "02", t: "Generic prep, real exams", d: "YouTube playlists and coaching classes that match no company's actual test pattern." },
  { k: "03", t: "First interview = the real one", d: "Your first proper interview happens in the placement drive itself, with everything at stake." },
];

const COLLEGE_PAINS = [
  { k: "01", t: "Readiness is invisible", d: "Which students are drive-ready? The answer arrives as a rejection list — after the drive." },
  { k: "02", t: "Recruiter outreach is guesswork", d: "Old contacts, cold emails, no idea whom to reach at which company this season." },
  { k: "03", t: "Spreadsheet placement cell", d: "Offer policies, NIRF numbers, drive pipelines — all policed by hand, disputed by everyone." },
];

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 32, filter: "blur(6px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  },
};

function StatCounter({ prefix = "", suffix = "", value, decimals = 0, label }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="flex flex-col gap-1"
    >
      <div className="font-display text-5xl md:text-6xl leading-none text-stone-100 flex items-baseline gap-1">
        {prefix && <span>{prefix}</span>}
        {inView ? (
          <CountUp value={value} duration={1800} />
        ) : (
          <span>0</span>
        )}
        {decimals > 0 && (
          <span className="text-3xl text-stone-400">.{Array(decimals).fill("0").join("")}</span>
        )}
        {suffix && (
          <span className="text-3xl text-amber-400">{suffix}</span>
        )}
      </div>
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-stone-400 mt-1">{label}</p>
    </motion.div>
  );
}

function PainCard({ item, badgeColor, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <motion.li
      ref={ref}
      initial={{ opacity: 0, x: badgeColor === "#C2410C" ? -24 : 24, filter: "blur(4px)" }}
      animate={inView ? { opacity: 1, x: 0, filter: "blur(0px)" } : {}}
      transition={{ duration: 0.65, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ scale: 1.02 }}
      className="group flex gap-5 rounded-2xl border border-stone-700/60 bg-stone-800/40 p-5 cursor-default transition-colors duration-300 hover:border-[#C2410C]/60"
    >
      <span
        className="font-display text-3xl leading-none w-12 shrink-0 transition-colors duration-300"
        style={{ color: "rgba(255,255,255,0.15)" }}
      >
        {item.k}
      </span>
      <div>
        <h3 className="font-display text-xl leading-snug text-stone-100 group-hover:text-white transition-colors duration-300">
          {item.t}
        </h3>
        <p className="mt-2 text-stone-400 text-sm leading-relaxed">{item.d}</p>
      </div>
      <div
        className="ml-auto w-1 rounded-full shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: badgeColor }}
      />
    </motion.li>
  );
}

export default function Problem() {
  const headlineRef = useRef(null);
  const headlineInView = useInView(headlineRef, { once: true, margin: "-80px" });
  const ctaRef = useRef(null);
  const ctaInView = useInView(ctaRef, { once: true, margin: "-60px" });

  return (
    <section className="relative bg-stone-900 py-24 md:py-36 overflow-hidden wsh-grain">
      {/* Ambient radials */}
      <div
        className="pointer-events-none absolute inset-0 -z-0 opacity-50"
        style={{
          background:
            "radial-gradient(700px 500px at 15% 10%, rgba(194,65,12,0.18), transparent 60%), radial-gradient(600px 400px at 85% 90%, rgba(21,128,61,0.12), transparent 60%)",
        }}
      />

      <div className="relative container-x">
        {/* ── Headline block ── */}
        <motion.div
          ref={headlineRef}
          initial={{ opacity: 0, y: 28 }}
          animate={headlineInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14 md:mb-20"
        >
          <span className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>
            The problem
          </span>
          <h2 className="font-display mt-4 text-4xl md:text-6xl lg:text-7xl leading-[1.0] text-stone-100">
            Placements are broken —{" "}
            <span className="italic text-stone-400">on both sides of campus</span>
          </h2>
        </motion.div>

        {/* ── Stat counters ── */}
        <div className="flex flex-col sm:flex-row gap-10 md:gap-20 mb-16">
          <StatCounter value={63} suffix="%" label="of graduates aren't job-ready, say employers" />
          <div className="w-px bg-stone-700 hidden sm:block" />
          <StatCounter value={1} prefix="#" label="factor parents check before admission: placements" />
        </div>

        {/* ── Pain columns ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {/* Candidate column */}
          <div className="md:pr-8 md:border-r md:border-stone-700/50">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 mb-7"
            >
              <span className="w-2.5 h-2.5 rounded-full bg-[#C2410C]" />
              <span className="font-mono text-xs uppercase tracking-[0.22em] text-stone-400">
                For students
              </span>
            </motion.div>
            <ul className="space-y-4">
              {CANDIDATE_PAINS.map((item, i) => (
                <PainCard
                  key={item.k}
                  item={item}
                  badgeColor="#C2410C"
                  index={i}
                />
              ))}
            </ul>
          </div>

          {/* Divider chevron (mobile: horizontal, desktop: centred vertically) */}
          <div className="flex md:hidden items-center justify-center py-8">
            <div className="flex flex-col items-center gap-1 text-stone-600">
              <ChevronDown size={20} />
              <ChevronDown size={20} className="-mt-3 opacity-50" />
            </div>
          </div>

          {/* College column */}
          <div className="md:pl-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-flex items-center gap-2 mb-7"
            >
              <span className="w-2.5 h-2.5 rounded-full bg-[#15803D]" />
              <span className="font-mono text-xs uppercase tracking-[0.22em] text-stone-400">
                For placement cells
              </span>
            </motion.div>
            <ul className="space-y-4">
              {COLLEGE_PAINS.map((item, i) => (
                <PainCard
                  key={item.k}
                  item={item}
                  badgeColor="#15803D"
                  index={i}
                />
              ))}
            </ul>
          </div>
        </div>

        {/* ── CTA bottom ── */}
        <motion.div
          ref={ctaRef}
          initial={{ opacity: 0, y: 24 }}
          animate={ctaInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 md:mt-20"
        >
          {/* Bridge panel: problem → solution. Lifted off the dark ground with a
              warm glow so the section ends on a deliberate note, not dead space. */}
          <div className="relative rounded-3xl overflow-hidden border border-amber-500/20 bg-gradient-to-br from-stone-800/80 via-stone-900/60 to-stone-900/80 p-8 md:p-12">
            <div
              className="absolute inset-0 pointer-events-none opacity-70"
              style={{ background: "radial-gradient(640px 300px at 12% 0%, rgba(245,158,11,0.22), transparent 62%), radial-gradient(520px 260px at 100% 100%, rgba(194,65,12,0.18), transparent 60%)" }}
            />
            <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-5">
                <p className="font-display text-4xl md:text-5xl text-stone-50 leading-[1.05]">
                  Wishire fixes this.
                </p>
                <p className="text-stone-300 mt-4 text-base md:text-lg leading-relaxed max-w-md">
                  One platform for both sides of campus — and a single readiness
                  score that students, placement cells and recruiters all trust.
                </p>
                <a
                  href="#wishire-score"
                  className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-amber-300 text-stone-900 font-semibold hover:bg-amber-200 transition-colors"
                >
                  See how the score works <ArrowRight size={16} />
                </a>
              </div>

              {/* The three pains above, answered one-to-one */}
              <ul className="lg:col-span-7 grid sm:grid-cols-3 gap-3">
                {[
                  { k: "01", t: "Readiness, visible early", d: "Every student scored months before drives — not discovered in the rejection list." },
                  { k: "02", t: "Prep that matches the exam", d: "Company-pattern mocks unlock for the exact drives a student is eligible for." },
                  { k: "03", t: "Policies that enforce themselves", d: "Offer caps, eligibility and NIRF numbers applied automatically at apply time." },
                ].map((f) => (
                  <li key={f.k} className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 backdrop-blur-sm">
                    <span className="font-mono text-[11px] tracking-[0.2em] text-amber-300">{f.k}</span>
                    <p className="text-stone-50 font-medium mt-2 leading-snug">{f.t}</p>
                    <p className="text-stone-400 text-sm mt-1.5 leading-relaxed">{f.d}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/** Renders ₹4.2L with the decimal animated in */
function CurrencyCounter() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="font-display text-5xl md:text-6xl leading-none text-stone-100 flex items-baseline gap-0.5"
    >
      <span className="text-amber-400">₹</span>
      <span>4</span>
      <span className="text-3xl text-stone-400">.2</span>
      <span className="text-3xl text-amber-400">L</span>
    </motion.div>
  );
}
